import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Business as OrganizationIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CheckCircle as StatusIcon,
  School as CertificationIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon
} from '@mui/icons-material';
import { reportsApi } from '../services/api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

interface ReportsProps {
  onBack: () => void;
}

interface ReportData {
  title: string;
  data: any[];
  summary?: any;
}

export const Reports: React.FC<ReportsProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [currentReport, setCurrentReport] = useState<ReportData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const reports = [
    {
      id: 'by-organization',
      title: 'Users by Organization',
      description: 'View users grouped by their organization',
      icon: <OrganizationIcon fontSize="large" color="primary" />,
      endpoint: 'by-organization'
    },
    {
      id: 'missing-zip-codes',
      title: 'Users with Missing Zip Codes',
      description: 'Find users who need zip code information',
      icon: <WarningIcon fontSize="large" color="warning" />,
      endpoint: 'missing-zip-codes'
    },
    {
      id: 'by-certification',
      title: 'Users by Certification',
      description: 'View users grouped by certification status',
      icon: <CertificationIcon fontSize="large" color="success" />,
      endpoint: 'by-certification'
    },
    {
      id: 'by-location',
      title: 'Users by Location',
      description: 'View users grouped by city and state',
      icon: <LocationIcon fontSize="large" color="secondary" />,
      endpoint: 'by-location'
    },
    {
      id: 'by-role',
      title: 'Users by Role',
      description: 'View users grouped by their role',
      icon: <PersonIcon fontSize="large" color="primary" />,
      endpoint: 'by-role'
    },
    {
      id: 'by-status',
      title: 'Users by Status',
      description: 'View active and inactive users',
      icon: <StatusIcon fontSize="large" color="info" />,
      endpoint: 'by-status'
    }
  ];

  const generateReport = async (reportId: string, title: string, endpoint: string) => {
    setLoading(true);
    try {
      const response = await reportsApi.getReport(endpoint);
      setCurrentReport({
        title,
        data: response.data,
        summary: response.summary || response
      });
      setDialogOpen(true);
    } catch (error) {
      console.error('Error generating report:', error);
      setToast({
        open: true,
        message: 'Failed to generate report',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!currentReport) return;

    let csvData: any[] = [];
    
    // Flatten the data based on report type
    if (currentReport.title.includes('Organization') || currentReport.title.includes('Role') || 
        currentReport.title.includes('Status') || currentReport.title.includes('Location')) {
      // For grouped reports, flatten the nested users
      currentReport.data.forEach((group: any) => {
        if (group.users && Array.isArray(group.users)) {
          group.users.forEach((user: any) => {
            csvData.push({
              ...user,
              group_name: group.organization || group.role || group.status || `${group.city}, ${group.state}`
            });
          });
        }
      });
    } else {
      // For flat reports (missing zip codes, certifications)
      csvData = currentReport.data;
    }

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${currentReport.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setToast({
      open: true,
      message: 'Report exported to CSV successfully',
      severity: 'success'
    });
  };

  const exportToExcel = () => {
    if (!currentReport) return;

    let excelData: any[] = [];

    // Flatten the data based on report type
    if (currentReport.title.includes('Organization') || currentReport.title.includes('Role') ||
        currentReport.title.includes('Status') || currentReport.title.includes('Location')) {
      // For grouped reports, flatten the nested users
      currentReport.data.forEach((group: any) => {
        if (group.users && Array.isArray(group.users)) {
          group.users.forEach((user: any) => {
            excelData.push({
              Group: group.organization || group.role || group.status || `${group.city}, ${group.state}`,
              Email: user.email,
              'First Name': user.first_name,
              'Last Name': user.last_name,
              Role: user.role || '-',
              Organization: user.organization_name || '-',
              City: user.city || '-',
              State: user.state || '-',
              'Zip Code': user.zip_code || '-',
              Phone: user.phone || '-',
              'Is Active': user.is_active ? 'Yes' : 'No',
              'LMS Enabled': user.lms_enabled ? 'Yes' : 'No',
              'RMS Enabled': user.rms_enabled ? 'Yes' : 'No'
            });
          });
        }
      });
    } else if (currentReport.title.includes('Certification')) {
      // For certification report
      currentReport.data.forEach((user: any) => {
        excelData.push({
          Email: user.email,
          'First Name': user.first_name,
          'Last Name': user.last_name,
          Role: user.role || '-',
          Organization: user.organization_name || '-',
          'Certificate Count': user.course_certificates_count || 0,
          'Has Final Exam': user.has_final_exam_cert > 0 ? 'Yes' : 'No',
          'Is Active': user.is_active ? 'Yes' : 'No'
        });
      });
    } else {
      // For missing zip codes report
      excelData = currentReport.data.map((user: any) => ({
        Email: user.email,
        'First Name': user.first_name,
        'Last Name': user.last_name,
        Role: user.role || '-',
        Organization: user.organization_name || '-',
        City: user.city || '-',
        State: user.state || '-',
        Phone: user.phone || '-',
        'Is Active': user.is_active ? 'Yes' : 'No'
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.min(Math.max(key.length, 10), maxWidth)
    }));
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `${currentReport.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);

    setToast({
      open: true,
      message: 'Report exported to Excel successfully',
      severity: 'success'
    });
  };

  const renderReportPreview = () => {
    if (!currentReport) return null;

    const { title, data, summary } = currentReport;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        {/* Summary Section */}
        {summary && (
          <Box mb={3}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {Object.entries(summary).map(([key, value]) => {
                if (typeof value === 'number' || typeof value === 'string') {
                  return (
                    <Box key={key} sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="textSecondary" variant="caption">
                            {key.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="h5">
                            {value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }
                return null;
              })}
            </Box>
          </Box>
        )}

        {/* Data Preview */}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {title.includes('Organization') && (
                  <>
                    <TableCell>Organization</TableCell>
                    <TableCell align="right">Total Users</TableCell>
                    <TableCell align="right">Active</TableCell>
                    <TableCell align="right">Inactive</TableCell>
                  </>
                )}
                {title.includes('Missing Zip') && (
                  <>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>City, State</TableCell>
                  </>
                )}
                {title.includes('Certification') && (
                  <>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell align="right">Certificates</TableCell>
                    <TableCell align="center">Final Exam</TableCell>
                  </>
                )}
                {title.includes('Location') && (
                  <>
                    <TableCell>State</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="right">User Count</TableCell>
                  </>
                )}
                {title.includes('Role') && (
                  <>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Total Users</TableCell>
                    <TableCell align="right">Active</TableCell>
                    <TableCell align="right">Inactive</TableCell>
                  </>
                )}
                {title.includes('Status') && (
                  <>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total Users</TableCell>
                    <TableCell align="right">LMS Enabled</TableCell>
                    <TableCell align="right">RMS Enabled</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.slice(0, 100).map((row: any, index: number) => (
                <TableRow key={index} hover>
                  {title.includes('Organization') && (
                    <>
                      <TableCell>{row.organization}</TableCell>
                      <TableCell align="right">{row.user_count}</TableCell>
                      <TableCell align="right">{row.active_count}</TableCell>
                      <TableCell align="right">{row.inactive_count}</TableCell>
                    </>
                  )}
                  {title.includes('Missing Zip') && (
                    <>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{`${row.first_name} ${row.last_name}`}</TableCell>
                      <TableCell>{row.organization_name || '-'}</TableCell>
                      <TableCell>{row.city && row.state ? `${row.city}, ${row.state}` : '-'}</TableCell>
                    </>
                  )}
                  {title.includes('Certification') && (
                    <>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{`${row.first_name} ${row.last_name}`}</TableCell>
                      <TableCell>{row.organization_name || '-'}</TableCell>
                      <TableCell align="right">{row.course_certificates_count || 0}</TableCell>
                      <TableCell align="center">
                        {row.has_final_exam_cert > 0 ? (
                          <Chip label="Yes" color="success" size="small" />
                        ) : (
                          <Chip label="No" color="default" size="small" />
                        )}
                      </TableCell>
                    </>
                  )}
                  {title.includes('Location') && (
                    <>
                      <TableCell>{row.state}</TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell align="right">{row.user_count}</TableCell>
                    </>
                  )}
                  {title.includes('Role') && (
                    <>
                      <TableCell>{row.role}</TableCell>
                      <TableCell align="right">{row.user_count}</TableCell>
                      <TableCell align="right">{row.active_count}</TableCell>
                      <TableCell align="right">{row.inactive_count}</TableCell>
                    </>
                  )}
                  {title.includes('Status') && (
                    <>
                      <TableCell>
                        <Chip
                          label={row.status}
                          color={row.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{row.user_count}</TableCell>
                      <TableCell align="right">{row.lms_enabled_count}</TableCell>
                      <TableCell align="right">{row.rms_enabled_count}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length > 100 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 100 of {data.length} rows. Export to see all data.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <ReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Reports
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Generate and export user reports in CSV or Excel format
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onBack}>
          Back to Users
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {reports.map((report) => (
          <Box key={report.id} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '400px' }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="center" mb={2}>
                  {report.icon}
                </Box>
                <Typography variant="h6" component="h2" gutterBottom align="center">
                  {report.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  {report.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<ReportIcon />}
                  onClick={() => generateReport(report.id, report.title, report.endpoint)}
                  disabled={loading}
                >
                  Generate
                </Button>
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Report Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Report Preview</Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderReportPreview()}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CsvIcon />}
            onClick={exportToCSV}
            variant="outlined"
            color="primary"
          >
            Export to CSV
          </Button>
          <Button
            startIcon={<ExcelIcon />}
            onClick={exportToExcel}
            variant="contained"
            color="success"
          >
            Export to Excel
          </Button>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

